import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useClusterApi } from '../api/ClusterApiProvider';
import { toApiError } from '../api/errors';
import type { ApiError } from '../api/errors';
import type { ClusterSession } from '../api/types';
import { addressWithoutCallback, readSignInCallback } from './clusterSignIn';

/**
 * The cluster session: whoever the tenancy's own identity provider vouched for.
 *
 * Two ways a page load can end up with one, and both are here because both are
 * about establishing a session and neither is a screen's business:
 *
 *   - an ordinary load asks the server who is signed in;
 *   - a load carrying `?code=&state=` is a return from the provider, and is
 *     finished before anything is drawn. Drawing the sign-in screen first and
 *     then swapping it out would show a person a way in they had just used.
 *
 * The exchange happens **once**. The code is spent by asking, so the address is
 * cleaned before the call rather than after it, and a guard keeps a second
 * render from posting the same code again — the second answer would be
 * `sign_in_expired`, a refusal about nothing on top of whatever really
 * happened.
 *
 * The way out is deliberately not behind the session: signing out has to work
 * when the session has already stopped working, or a person is left holding a
 * cookie they cannot get rid of.
 */

type Status = 'loading' | 'ready';

export interface ClusterSessionState {
  status: Status;
  session: ClusterSession | null;
  /** Why the last return from the provider produced no session, if it did not. */
  signInFailure: ApiError | null;
  signOut: () => Promise<void>;
}

const ClusterSessionContext = createContext<ClusterSessionState | null>(null);

export function ClusterSessionProvider({ children }: { children: ReactNode }) {
  const api = useClusterApi();
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<ClusterSession | null>(null);
  const [signInFailure, setSignInFailure] = useState<ApiError | null>(null);

  /**
   * One exchange per return, whatever the renderer does with this component.
   *
   * **Released when the effect is torn down**, and that is not a detail. React's
   * strict mode mounts an effect, tears it down and mounts it again; a latch
   * that stayed shut would let the first run abort its own request and the
   * second run do nothing at all, leaving the console on a spinner forever.
   * Releasing it costs one repeated `getSession` in development and nothing in
   * a build.
   *
   * The authorization code is still exchanged once: the address is cleared
   * before the exchange starts, so a second run reads no callback and asks for
   * the session instead.
   */
  const settled = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    settled.current = true;

    const controller = new AbortController();
    const callback = readSignInCallback(window.location.search);

    if (callback === null) {
      api.getSession(controller.signal).then(
        (value) => {
          if (controller.signal.aborted) return;
          setSession(value);
          setStatus('ready');
        },
        () => {
          // No session and no server both mean the same thing here: show the
          // way in rather than a spinner nobody can get past.
          if (controller.signal.aborted) return;
          setSession(null);
          setStatus('ready');
        },
      );
      return () => {
        controller.abort();
        settled.current = false;
      };
    }

    window.history.replaceState(null, '', addressWithoutCallback(window.location));

    api.completeSignIn(callback).then(
      (value) => {
        if (controller.signal.aborted) return;
        setSession(value);
        setStatus('ready');
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setSignInFailure(toApiError(error));
        setSession(null);
        setStatus('ready');
      },
    );

    return () => {
      controller.abort();
      settled.current = false;
    };
  }, [api]);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      setSession(null);
      setSignInFailure(null);
    }
  }, [api]);

  const value = useMemo<ClusterSessionState>(
    () => ({ status, session, signInFailure, signOut }),
    [status, session, signInFailure, signOut],
  );

  return <ClusterSessionContext value={value}>{children}</ClusterSessionContext>;
}

export function useClusterSession(): ClusterSessionState {
  const value = useContext(ClusterSessionContext);
  if (value === null) {
    throw new Error('useClusterSession() used outside <ClusterSessionProvider>');
  }
  return value;
}
